import { isTouchDevice } from '../user/device-info';

const disableAutomaticClosing = false;

export class MegaMenu {
    _root: HTMLElement;
    _links: NodeListOf<HTMLLIElement>;
    _linkAnchors: NodeListOf<HTMLAnchorElement>;
    _menus: Record<string, HTMLElement> = {};
    _activeMenuID: string;
    _linkEnterTimeout: number | null = null;

    _globalClickHandler: ( event: Event ) => void;
    _globalLeaveHandler: ( event: Event ) => void;

    constructor( root: HTMLElement ) {
        this._root = root;
        this._links = root.querySelectorAll<HTMLLIElement>( '.siteheader__navlinks .siteheader__primarylink' );
        this._linkAnchors = root.querySelectorAll<HTMLAnchorElement>( '.siteheader__navlinks .siteheader__primarylink a' );

        root.querySelectorAll( '.siteheader__menuitem' ).forEach( ( menu: HTMLElement ) => {
            if ( !menu.dataset.id ) {
                return;
            }

            this._menus[ menu.dataset.id ] = menu;
        } );

        this._globalClickHandler = this._onDocumentClick.bind( this );
        this._globalLeaveHandler = this.hide.bind( this );

        this._init();
    }

    _init() {
        this._links.forEach( ( link: HTMLLIElement ) => {
            link.addEventListener( 'mouseleave', this._onLinkLeave.bind( this ) );
            link.addEventListener( 'mouseenter', this._onLinkEnter.bind( this ) );
        } );

        if ( isTouchDevice() && this._linkAnchors.length > 0 ) {
            this._linkAnchors.forEach( ( a: HTMLAnchorElement ) => {
                if ( a.closest<HTMLLIElement>( '.siteheader__iconlink' ) ) {
                    return;
                }

                a.addEventListener( 'click', ( event: PointerEvent ) => {
                    if ( event.pointerType && event.pointerType === 'touch' && event.target && event.target instanceof Element && ( event.target.matches( '.siteheader__primarylinkanchor' ) || event.target.closest( '.siteheader__primarylinkanchor' ) ) ) {
                        event.preventDefault();
                    }
                } );
            } );
        }

        this._root.addEventListener( 'close-menu', this.hide.bind( this ) );

        if ( !disableAutomaticClosing ) {
            this._root.addEventListener( 'mouseleave', () => {
                if ( this._linkEnterTimeout ) {
                    window.clearTimeout( this._linkEnterTimeout );
                }
            } );
        }

        window.addEventListener( 'pageshow', ( event: PageTransitionEvent ) => {
            if ( event.persisted ) {
                this.hide();
            }
        } );
    }

    _onLinkLeave( event: Event ): void {
        if ( !event.target ) {
            return;
        }

        const target = event.target as HTMLLIElement;

        if ( !target.dataset.menuId ) {
            return;
        }

        if ( this._linkEnterTimeout ) {
            window.clearTimeout( this._linkEnterTimeout );
            this._linkEnterTimeout = null;
        }
    }

    _onLinkEnter( event: Event ): void {
        if ( !event.target ) {
            return;
        }

        const target = event.target as HTMLLIElement;

        if ( !target.dataset.menuId ) {
            this.hide();
            return;
        }

        const menuID = target.dataset.menuId;

        if ( menuID === this._activeMenuID ) {
            return;
        }

        this._linkEnterTimeout = window.setTimeout( () => {
            this._linkEnterTimeout = null;
            this.activateMenu( menuID );
        }, 250 );
    }

    _onDocumentClick( event: Event ): void {
        if ( !this.isVisible ) {
            return;
        }

        if ( !event.target || !( event.target instanceof HTMLElement ) ) {
            return;
        }

        const target  = event.target as HTMLElement;

        if ( target.closest( '.siteheader' ) ) {
            return;
        }

        this.hide();
    }

    activateMenu( menuID: string ): void {
        this._links.forEach( ( link: HTMLElement ) => link.classList.remove( 'siteheader__primarylink--active' ) );

        document.querySelectorAll( `.siteheader__primarylink[data-menu-id="${menuID}"]` ).forEach( ( item: HTMLLIElement ) => {
            item.classList.add( 'siteheader__primarylink--active' );
        } );

        this._menus[ menuID ].classList.add( 'siteheader__menuitem--active' );

        let zIndex = 0;

        if ( this._activeMenuID ) {
            zIndex = parseInt( this._menus[ this._activeMenuID ].style.zIndex ) + 1;
        }

        this._menus[ menuID ].style.zIndex = zIndex.toString();

        if ( this._activeMenuID ) {
            this._menus[ this._activeMenuID ].classList.remove( 'siteheader__menuitem--active' );
            this._menus[ this._activeMenuID ].style.zIndex = '';
        }

        this._activeMenuID = menuID;

        this.show();
    }

    show(): void {
        if ( this.isVisible ) {
            return;
        }

        this._root.classList.add( 'siteheader--visible' );

        document.addEventListener( 'click', this._globalClickHandler );

        if ( !disableAutomaticClosing ) {
            this._root.addEventListener( 'mouseleave', this._globalLeaveHandler );
        }
    }

    hide(): void {
        if ( !this.isVisible ) {
            return;
        }

        if ( this._linkEnterTimeout ) {
            window.clearTimeout( this._linkEnterTimeout );
        }

        this._root.classList.remove( 'siteheader--visible' );

        if ( this._activeMenuID ) {
            this._menus[ this._activeMenuID ].classList.remove( 'siteheader__menuitem--active' );
            this._links.forEach( ( link: HTMLElement ) => link.classList.remove( 'siteheader__primarylink--active' ) );
            this._activeMenuID = '';
        }

        document.removeEventListener( 'click', this._globalClickHandler );

        document.removeEventListener( 'click', this._globalLeaveHandler );
    }

    get isVisible(): boolean {
        return this._root.classList.contains( 'siteheader--visible' );
    }
}

export function closeHeaderMenu(): void {
    const activeMenuItem = document.querySelector( '.siteheader__menuitem--active' );

    if ( activeMenuItem ) {
        activeMenuItem.dispatchEvent( new CustomEvent( 'close-menu', {
            bubbles: true,
        } ) );
    }
}
